import { useMemo } from 'react'
import { generatePath } from 'react-router-dom'
import { useReadDocumentRequestList } from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { routes } from '@/router'
import {
  DocumentRequest,
  DocumentRequestStatus,
  DocumentRequestStatusEnum,
} from '@/types'
import { Box, Card, Stack, Typography } from '@mui/material'
import { RouterLink } from '@/components'
import { ErrorOutline } from '@mui/icons-material'
import { capitalize, formatDate } from '@/helpers'
import { MUIColors } from '@/styles/theme'

const COLOR_BY_STATUS: Record<DocumentRequestStatus, MUIColors> = {
  approved: 'success',
  pending: 'warning',
  pending_review: 'info',
}

type DocumentsByYear = {
  [key: string]: DocumentRequest[]
}

export function DocumentRequestList() {
  const { company } = useCompany()

  const { data: document_requests, error } = useReadDocumentRequestList({
    company_key: company.key,
  })

  // format document requests to group by year
  const documentsByYear = useMemo(() => {
    // create an object with document requests grouped by year
    const groupedByMonth = document_requests.reduce((acc, doc) => {
      const year = doc.year

      if (!acc[year]) {
        acc[year] = []
      }

      acc[year].push(doc)

      return acc
    }, {} as DocumentsByYear)

    // return an array with the years and the respective document requests
    return Object.keys(groupedByMonth)
      .map((year) => ({
        year,
        documents_requests: groupedByMonth[year].sort(
          (a, b) => Number(b.month) - Number(a.month),
        ), // sort document requests by month in desc order,
      }))
      .sort((a, b) => Number(b.year) - Number(a.year)) // sort document requests by year in desc order
  }, [document_requests])

  if (error) {
    return (
      <Box textAlign="center">
        <ErrorOutline color="error" fontSize="large" />
        <Typography color="gray">
          Não foi possível carregar o movimento mensal.
        </Typography>
      </Box>
    )
  }

  if (documentsByYear.length === 0) {
    return (
      <Typography color="gray" textAlign="center" py={2}>
        Não encontramos nenhuma pendência
      </Typography>
    )
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 0 } }}>
      <Stack spacing={2}>
        {documentsByYear.map(({ year, documents_requests }) => (
          <Stack key={year} spacing={1}>
            <Typography variant="h5">{year}</Typography>

            {documents_requests.map(({ key, status, month, year }) => {
              const isApproved = status === 'approved'
              const date = new Date(Number(year), Number(month) - 1, 1)
              return (
                <Card
                  key={key}
                  component={RouterLink}
                  to={
                    isApproved
                      ? ''
                      : generatePath(routes.accounting.monthlyDocumentsUpload, {
                          document_request_key: key,
                        })
                  }
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    cursor: isApproved ? 'default' : 'pointer',
                    mx: { xs: 0.5, sm: 0 },
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography>
                    {capitalize(formatDate(date, 'MMMM'))}
                  </Typography>
                  <Typography color={COLOR_BY_STATUS[status]}>
                    {DocumentRequestStatusEnum[status]}
                  </Typography>
                </Card>
              )
            })}
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}
